<template>
  <div>
    <page-list
      :list="list"
      :columns="filteredColumns"
      show-tag-columns2
      tagColumn2Resource="identity-metadatas"
      :tagColumn2ParamsFormatter="tagColumnParamsFormatter"
      :tagColumns2Generator="tagColumns2Generator"
      :group-actions="groupActions"
      :single-actions="singleActions"
      :showSearchbox="showSearchbox"
      :showGroupActions="showGroupActions"
      :export-data-options="exportDataOptions">
      <template v-slot:group-actions-append>
        <div class="d-flex align-items-center">
          <date-time
            class="ml-2"
            :getParams.sync="dateParam"
            :dateMode.sync="dateMode"
            start="start_day"
            end="end_day"
            paramTimeFormatter="YYYYMMDD"
            @refresh="fetchData" />
          <div class="ml-2">
            <span>{{$t('bill.project_sharing_records_show_all')}}</span> <help-tooltip :text="$t('bill.project_sharing_records_show_all_tips')" /> :
            <a-switch class="ml-1" v-model="showAll" @change="changeShowAll" />
          </div>
          <div class="ml-2" v-if="showGroupByTags">
            <span>{{$t('bill.group_with_tag')}}</span> :
            <a-switch class="ml-1" v-model="groupByProjectTag" @change="changeGroupByProjectTag" />
          </div>
        </div>
      </template>
      <template v-slot:table-prepend>
        <total-price
          :label="$t('projectType.default')"
          :secondaryLabel="$t('projectType.sharing')"
          :currencySign="currencySign"
          :secondaryTotalShown="true"
          :total="total_pre_amount"
          :secondary_total="total_share_amount" />
      </template>
    </page-list>
  </div>
</template>

<script>
import * as R from 'ramda'
import { mapState } from 'vuex'
import TotalPrice from '@Bill/sections/TotalPrice'
import currencyRequest from '@Bill/mixins/currencyRequest'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { currencyUnitMap } from '@/constants/currency'
import { numerify } from '@/filters'
import { getCustomDistinctFieldFilter } from '@/utils/common/tableFilter'
import { getBillResourceTypeLabel } from '@Bill/utils'

export default {
  name: 'ProjectSharingRecordList',
  components: {
    TotalPrice,
  },
  mixins: [WindowsMixin, ListMixin, currencyRequest],
  props: {
    resId: {
      type: String,
      require: true,
    },
    accountData: Object,
    id: String,
    data: Object,
    getParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      dateMode: 'month',
      dateParam: {},
      total_pre_amount: 0,
      total_share_amount: 0,
      showAll: false,
      groupByProjectTag: false,
      list: this.$list.createList(this, {
        id: 'SharingRecordForCloudaccountSidepage',
        apiVersion: 'v1',
        resource: 'payment_bills/project-sharing-status-list',
        getParams: this.getParam,
        fetchDataCb: this.getTotal,
        noPreLoad: true,
        filterOptions: {
          project_id: getCustomDistinctFieldFilter({
            label: this.$t('bill.sharing_target_project'),
            field: 'project',
            fetchMethod: this.distinctFetcher('project'),
            disabledFormatter: true,
            mapper: (list) => {
              return list.map(item => {
                const arr = item.key.split('/')
                return {
                  key: arr[1] || '',
                  label: arr[0] || '',
                }
              })
            },
          }),
          domain: getCustomDistinctFieldFilter({
            label: this.$t('bill.sharing_target_project_domain'),
            field: 'domain',
            fetchMethod: this.distinctFetcher('domain'),
            disabledFormatter: true,
          }),
          resource_type: getCustomDistinctFieldFilter({
            label: this.$t('bill.text_73'),
            field: 'resource_type',
            fetchMethod: this.distinctFetcher('resource_type'),
            disabledFormatter: true,
          }),
          origin_res_type: getCustomDistinctFieldFilter({
            label: this.$t('bill.sharing.origin_res_type'),
            field: 'origin_res_type',
            fetchMethod: this.distinctFetcher('origin_res_type'),
            disabledFormatter: true,
          }),
          product_detail_code: getCustomDistinctFieldFilter({
            label: this.$t('bill.sharing.product_detail_code'),
            field: 'product_detail_code',
            fetchMethod: this.distinctFetcher('product_detail_code'),
            disabledFormatter: true,
          }),
        },
      }),
    }
  },
  computed: {
    ...mapState('bill', {
      billShared: state => state.billShared,
    }),
    ...mapState('common', {
      billConfig: state => state.bill.globalConfig,
    }),
    columns () {
      return [
        {
          field: 'project',
          title: this.$t('bill.sharing_target_project'),
        },
        {
          field: 'domain',
          title: this.$t('bill.sharing_target_project_domain'),
        },
        {
          field: 'resource_type',
          title: this.$t('bill.text_73'),
          formatter: ({ row }) => {
            return getBillResourceTypeLabel(row.resource_type)
          },
        },
        {
          field: 'origin_res_type',
          title: this.$t('bill.sharing.origin_res_type'),
          formatter: ({ row }) => {
            return row.origin_res_type || '-'
          },
        },
        {
          field: 'product_detail_code',
          title: this.$t('bill.sharing.product_detail_code'),
          formatter: ({ row }) => {
            return row.product_detail_code || '-'
          },
        },
        {
          field: 'pre_amount',
          title: this.$t('projectType.default'),
          slots: {
            default: ({ row }) => {
              const value = numerify(row.pre_amount || 0, '0,0.00')
              return this.currencySign ? `${this.currencySign} ${value}` : value
            },
          },
          formatter: ({ row }) => {
            const value = numerify(row.pre_amount || 0, 'originBill')
            return this.currencySign ? `${this.currencySign} ${value}` : value
          },
          needExportFormat: true,
        },
        {
          field: 'share_amount',
          title: this.$t('projectType.sharing'),
          slots: {
            default: ({ row }) => {
              const value = numerify(row.share_amount || 0, '0,0.00')
              return this.currencySign ? `${this.currencySign} ${value}` : value
            },
          },
          formatter: ({ row }) => {
            const value = numerify(row.share_amount || 0, 'originBill')
            return this.currencySign ? `${this.currencySign} ${value}` : value
          },
          needExportFormat: true,
        },
      ]
    },
    exportDataOptions () {
      const items = this.columns.map(item => {
        return {
          key: item.field,
          label: item.title,
          formatter: item.formatter,
          needExportFormat: item.needExportFormat,
        }
      })
      return {
        title: this.$t('bill.bill_sharing_policy'),
        downloadType: 'local',
        items,
      }
    },
    currencySign () {
      const currency = this.currency || this.data.currency
      if (currencyUnitMap[currency]) {
        return currencyUnitMap[currency].sign || ''
      }
      return ''
    },
    projectTagParams () {
      const ret = {}
      if (this.list.config && this.list.config.showProjectTagKeys && this.list.config.showProjectTagKeys.length) {
        ret.project_tags = this.list.config.showProjectTagKeys
      }
      if (this.groupByProjectTag) {
        ret.group_by_project_tags = true
      }
      return ret
    },
    filteredColumns () {
      return this.columns.filter(item => {
        return this.groupByProjectTag ? ['pre_amount', 'share_amount'].includes(item.field) : true
      })
    },
    showGroupByTags () {
      if (this.list && this.list.config && this.list.config.showProjectTagKeys && this.list.config.showProjectTagKeys.length) {
        return true
      }
      return false
    },
  },
  watch: {
    'list.config.showProjectTagKeys': {
      handler (val) {
        this.list.fetchData()
        if (!val || !val.length) {
          this.groupByProjectTag = false
        }
      },
      deep: true,
    },
  },
  created () {
    this.$dM = new this.$Manager('payment_bills/project-sharing-status-list-distinct-field')
    this.fetchData()
  },
  methods: {
    tagColumnParamsFormatter (params) {
      const ret = { ...params, key_only: true }
      delete ret.with_user_meta
      return ret
    },
    tagColumns2Generator (keys) {
      const columns = keys.map((key, index) => {
        return {
          field: key,
          key: `projectTag:${key}`,
          title: key.replace('user:', '').replace('ext:', ''),
          label: key.replace('user:', '').replace('ext:', ''),
          slots: {
            tag_type: () => {
              return 'project'
            },
            default: ({ row }) => {
              return row[`tag${index}`] || '-'
            },
          },
          formatter: ({ row }) => {
            return row[`tag${index}`] || '-'
          },
        }
      })
      return columns
    },
    distinctFetcher (field) {
      const vm = this
      return async function () {
        const params = {
          ...vm.getParam(),
          scope: vm.$store.getters.scope,
          field,
        }
        delete params.group
        const response = await vm.$dM.list({
          params,
        })
        return {
          data: {
            [field]: response.data || [],
          },
        }
      }
    },
    fetchData () {
      this.list.fetchData()
    },
    getParam () {
      const getParams = (R.is(Function, this.getParams) ? this.getParams() : this.getParams)
      const ret = {
        details: false,
        account_id: this.resId,
        exchanged_currency: this.currencyParams.exchanged_currency,
        is_prepaid_amortizing: this.billShared,
        ...getParams,
        ...this.dateParam,
        group: ['resource_type', 'origin_res_type', 'product_detail_code'],
        ...this.projectTagParams,
      }
      if (this.showAll) {
        ret.all = true
      } else {
        ret.all = false
      }
      return ret
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'ProjectsharingSidePage', {
        id: row.id,
        resource: 'project_sharings',
        apiVersion: 'v1',
        getParams: this.getParams,
      }, {
        list: this.list,
        hiddenActions: this.hiddenActions,
      })
    },
    getTotal (response) {
      const { totals = {} } = response.data
      const { total_pre_amount = 0, total_share_amount = 0 } = totals
      this.total_pre_amount = total_pre_amount
      this.total_share_amount = total_share_amount
    },
    changeShowAll (e) {
      this.fetchData()
    },
    changeGroupByProjectTag (e) {
      this.fetchData()
    },
  },
}
</script>
